'use client'
import { axiosAdmin } from "@/lib/axios";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";


export function useGetProductsById(id: string) {
  return useQuery({
    queryKey: ['getsingleproduct', id],
    queryFn: async (id) => {
      const m = id.queryKey[1];
      try {
        if (m) {
          const response = await axiosAdmin.get(`/user/products/${m}`);

          return response.data;
        }
        return null
      } catch (error) {
        throw error;
      }
    }
  });
}


export function useDebouncedSearchAll(
  searchTerm: string,
  delay = 300,
  type: "dropdown" | "page" = "dropdown",
  page: number = 1,
  limit: number = 20,
  sort?: string,
  categories?: string[],
  brands?: string[]
) {
  const [debouncedTerm, setDebouncedTerm] = useState(searchTerm);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedTerm(searchTerm);
    }, delay);
    return () => clearTimeout(handler);
  }, [searchTerm, delay]);

  return useQuery({
    queryKey: ['searchall', debouncedTerm, type, page, limit, sort, categories, brands],
    queryFn: async ({ queryKey }) => {
      const term = queryKey[1] as string;
      const searchType = queryKey[2] as string;
      const pageNum = queryKey[3] as number;
      const limitNum = queryKey[4] as number;

      if (!term.trim()) {
        return searchType === "page"
          ? { enrichedProducts: [], categories: [], brands: [], total: 0, page: 1, limit: limitNum, totalPages: 0 }
          : { enrichedProducts: [], categories: [], brands: [] };
      }

      const params = new URLSearchParams({
        q: term,
        type: searchType,
      });

      if (searchType === "page") {
        params.append("page", pageNum.toString());
        params.append("limit", limitNum.toString());
      }

      if (sort) params.append("sort", sort);
      if (categories && categories.length > 0) params.append("categories", categories.join(","));
      if (brands && brands.length > 0) params.append("brands", brands.join(","));

      const res = await axiosAdmin.get(`/user/products/search?${params.toString()}`);
      return res.data;
    },
    enabled: !!debouncedTerm.trim(),
    staleTime: 60_000,
  });
}

export const useSearchProducts = (query: string) => {
  return useQuery({
    queryKey: ["searchProducts", query],
    queryFn: async () => {
      if (!query) return []
      const { data } = await axiosAdmin.get(`user/products/search?q=${query}`)
      return data
    },
    enabled: !!query,
  })
}


interface FilterParams {
  brands?: string
  categories?: string
}


export const useFilterSearchProducts = (filters: FilterParams) => {
  return useQuery({
    queryKey: ["filterProducts", filters],
    queryFn: async () => {
      const { data } = await axiosAdmin.get(`/user/products/filter`, {
        params: {
          ...(filters.brands ? { brands: filters.brands } : {}),
          ...(filters.categories ? { categories: filters.categories } : {}),
        },
      })
      return data
    },
    enabled: !!(filters.brands || filters.categories),
  })
}


export function useGetProductBySlug(slug: string) {
  return useQuery({
    queryKey: ['getProductBySlug', slug],
    queryFn: async () => {
      try {
        if (slug) {
          const response = await axiosAdmin.get(`/user/products/slug/${slug}`);
          return response.data;
        }
        return null;
      } catch (error) {
        throw error;
      }
    },
    enabled: !!slug,
  });
}
export function useGetAllFirstOrderAmount() {
  return useQuery({
    queryKey: ['getallfirstorderamount'],
    queryFn: async () => {
      const response = await axiosAdmin.get("/user/products/first-order-amount");
      return response?.data;
    }
  });
}